/**
 * Domain: note length (duration).
 *
 * We represent note length as a duration in milliseconds for MIDI scheduling.
 * Pure logic only.
 */

/**
 * Settings for normalizing note length.
 */
export interface NoteLengthSettings {
  defaultMs: number;
  minMs: number;
  maxMs: number;
}

/**
 * Clamp to inclusive [min, max].
 */
function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Normalize a note length for "staccato" mode.
 *
 * @param rawMs - Raw note length value (may be non-numeric)
 * @param settings - Normalization settings
 * @returns Integer milliseconds
 */
export function normalizeNoteLengthMs(
  rawMs: unknown,
  settings: NoteLengthSettings
): number {
  if (!settings) throw new Error('settings is required');
  const { defaultMs, minMs, maxMs } = settings;

  if (!Number.isFinite(defaultMs) || defaultMs <= 0) {
    throw new Error('defaultMs must be a positive number');
  }
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs < minMs) {
    throw new Error('minMs/maxMs must be finite and maxMs must be >= minMs');
  }

  const n = Number(rawMs);
  if (!Number.isFinite(n)) return Math.round(clamp(defaultMs, minMs, maxMs));
  return Math.round(clamp(n, minMs, maxMs));
}

